import { Link } from "react-router-dom";
import { ChevronRight, Sparkles, Check, ArrowRight, Phone } from "lucide-react";
import { useState } from "react";
import Reveal from "../components/Reveal";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { ASSETS, BRAND } from "../lib/brand";

const TABS = [
  { id: "billing", label: "Billing & POS", items: [
    "Fast & user-friendly billing interface", "Touch screen POS support", "Multi-counter billing management",
    "Barcode-based billing", "GST-compliant invoicing", "Credit and cash sales management",
    "Split payment & multiple payment modes", "Returns and exchange management",
  ]},
  { id: "inventory", label: "Inventory & Stock", items: [
    "Real-time stock monitoring", "Multi-warehouse management", "Batch & expiry tracking",
    "Stock transfer between branches", "Repacking & product conversion", "Physical stock verification",
    "Automatic stock adjustments", "Purchase order management",
  ]},
  { id: "customer", label: "Customer", items: [
    "Customer database management", "Loyalty points system", "Membership & reward programs",
    "Customer purchase history", "Promotional campaigns", "Offer & discount management",
  ]},
  { id: "whatsapp", label: "WhatsApp", items: [
    "Digital bill sharing through WhatsApp", "Customer communication & promotions",
    "Automated transaction notifications", "Marketing message integration",
  ]},
  { id: "gst", label: "GST & Compliance", items: [
    "Automatic E-Invoice generation", "Automatic E-Way Bill generation",
    "GST-ready billing and reporting", "Tax compliance management",
  ]},
  { id: "accounting", label: "Accounting", items: [
    "Seamless Tally integration", "Automated accounting entries", "Supplier ledger management",
    "Customer ledger management", "Profit & loss analysis", "Outstanding management",
  ]},
  { id: "reports", label: "Reports", items: [
    "Real-time sales reports", "Profitability reports", "Product performance analysis",
    "User activity monitoring", "Branch-wise performance reports", "Stock movement reports",
  ]},
  { id: "multi", label: "Multi-Branch", items: [
    "Centralized control of multiple outlets", "Branch synchronization",
    "Unified reporting system", "Central inventory visibility",
  ]},
  { id: "security", label: "Security", items: [
    "Role-based user permissions", "User activity tracking", "Audit logs", "Transaction approval workflows",
  ]},
  { id: "ideal", label: "Ideal For", items: [
    "Supermarkets", "Hypermarkets", "Grocery Stores", "Textiles", "Footwear Stores",
    "Mobile Shops", "Hardware Stores", "Pharmacies", "Wholesale Businesses", "Distribution Companies",
  ]},
];

export default function DigiposZen() {
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);

  return (
    <div data-testid="digipos-zen-page">
      <section className="relative gi-hero-gradient text-white pt-32 pb-24">
        <div className="absolute inset-0 gi-mesh opacity-40" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="flex items-center gap-2 text-white/60 text-sm mb-4">
                <Link to="/" className="hover:text-white">Home</Link>
                <ChevronRight className="h-3 w-3" />
                <Link to="/digipos" className="hover:text-white">DigiPOS</Link>
                <ChevronRight className="h-3 w-3" />
                <span className="text-white">Zen</span>
              </div>
              <span className="gi-pill-dark"><Sparkles className="h-3.5 w-3.5" /> Flagship Product</span>
              <h1 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl mt-4 leading-tight">DigiPOS Zen</h1>
              <p className="font-display text-xl text-[#00B4D8] mt-2">Complete Retail & Distribution ERP Solution</p>
              <p className="text-white/75 mt-5 max-w-xl">
                A powerful Windows-based ERP and billing software for retailers, wholesalers, distributors and multi-location businesses. Supports offline and online operation — uninterrupted business continuity, always.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link to="/contact" data-testid="zen-demo-btn" className="gi-btn-primary">Request Demo <ArrowRight className="h-4 w-4" /></Link>
                <button
                    className="gi-btn-ghost"
                    data-testid="zen-brochure-btn"
                    onClick={() => {
                      window.open("/brochures/DigiPosZen.pdf", "_blank");
                    }}
                  >
                    Download Brochure
                  </button>
                <div className="relative">
                  <button
                    className="gi-btn-ghost"
                    onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                  >
                    Download App ▼
                  </button>

                  {showDownloadMenu && (
                    <div
                      className="absolute top-full left-0 mt-2 min-w-[220px]
                rounded-xl overflow-hidden
                border border-white/20
                bg-white/5
                backdrop-blur-sm
                shadow-lg
                z-50"
                    >
                      <button
                        className="block w-full text-left px-4 py-3 text-white hover:bg-white/10 transition"
                        onClick={() => {
                          window.open(
                            "https://drive.google.com/drive/folders/10ImdYHBa21_YlOgd2YDbbHSZL0iSaCec?usp=drive_link",
                            "_blank"
                          );
                          setShowDownloadMenu(false);
                        }}
                      >
                        Download Setups
                      </button>

                      <button
                        className="block w-full text-left px-4 py-3 text-white hover:bg-white/10 transition border-t border-white/10"
                        onClick={() => {
                          window.open("https://drive.google.com/drive/folders/1viDOIQAGaOeKdGAUYLm3-UHJO0SnNxNH?usp=drive_link", "_blank");
                          setShowDownloadMenu(false);
                        }}
                      >
                        Download Updates
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-7 flex flex-wrap gap-2">
                {["Windows", "Cloud / Static IP", "Offline + Online"].map((t) => (
                  <span key={t} className="font-mono text-xs uppercase tracking-wider px-3 py-1.5 rounded-full bg-white/10 border border-white/15">{t}</span>
                ))}
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={120}>
              <div className="relative gi-float">
                <div className="absolute -inset-4 bg-gradient-to-br from-[#1565C0]/40 to-[#00B4D8]/30 blur-3xl rounded-3xl" />
                <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl bg-white">
                  <img src={ASSETS.zen} alt="DigiPOS Zen" className="w-full h-auto block" />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-white gi-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal>
            <span className="font-mono text-xs uppercase tracking-widest text-[#1565C0]">Features</span>
            <h2 className="font-display font-bold text-3xl md:text-4xl mt-2 mb-3">Everything Zen does</h2>
            <p className="text-[#6B7280] max-w-2xl">Explore Zen&apos;s capabilities by module — billing to reporting to multi-branch control.</p>
          </Reveal>

          <Reveal delay={80}>
            <Tabs defaultValue={TABS[0].id} className="mt-10">
              <TabsList className="flex flex-wrap h-auto justify-start gap-2 bg-[#F5F8FF] p-2 rounded-xl">
                {TABS.map((t) => (
                  <TabsTrigger
                    key={t.id}
                    value={t.id}
                    data-testid={`zen-tab-${t.id}`}
                    className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#1565C0] data-[state=active]:to-[#00B4D8] data-[state=active]:text-white text-sm"
                  >
                    {t.label}
                  </TabsTrigger>
                ))}
              </TabsList>
              {TABS.map((t) => (
                <TabsContent key={t.id} value={t.id} className="mt-8">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {t.items.map((item) => (
                      <div key={item} className="flex items-start gap-3 p-4 rounded-xl border border-[#DDE6F0] bg-[#F5F8FF] hover:border-[#1565C0]/40 hover:bg-white transition">
                        <Check className="h-5 w-5 text-[#10B981] mt-0.5 shrink-0" />
                        <span className="text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </Reveal>
        </div>
      </section>

      <section className="gi-deep-gradient text-white gi-section">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 text-center">
          <Reveal>
            <h2 className="font-display font-bold text-3xl md:text-4xl">Ready to see DigiPOS Zen in action?</h2>
            <p className="text-white/70 mt-4 max-w-xl mx-auto">Book a free, no-obligation demo customised to your business.</p>
            <div className="mt-7 flex flex-wrap justify-center gap-3">
              <Link to="/contact" data-testid="zen-bottom-demo" className="gi-btn-primary">Request a Free Demo <ArrowRight className="h-4 w-4" /></Link>
              <a href={`tel:${BRAND.phone1}`} className="gi-btn-ghost"><Phone className="h-4 w-4" /> Call Us: {BRAND.phone1}</a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
